import {
  IStatementCurrencySection,
  IStatementLine,
  StatementLineType,
  currencyFormatter,
  getStatementSplitData,
  toDecimal,
} from "shared";
import { When } from "react-if";
import { Tooltip } from "@mui/material";
import { ExclamationCircleIcon } from "@heroicons/react/20/solid";

import { CardContent } from "../../../../../../common/Atoms/CardContent";
import { classNames } from "../../../../../../common/lib/classNames";
import { WrappedLink } from "../../../../WrappedLink";
import { LoadingOverlay } from "../../../../Molecules/LoadingOverlay";
import { HelpIcon } from "../../../../Molecules/HelpIcon";
import { DatePopover } from "../../../../../../common/Components/DatePopover";
import { Badge } from "../../../../../../common/Atoms/Badge";

interface ICurrentStatementProps {
  currencySection: IStatementCurrencySection;
  loading?: boolean;
  children?: React.ReactNode;
  organisationTimezone: string;
  omitColumns?: Array<`payments`>;
  childContacts?: { id?: number; name?: string }[];
  showSplitBalances?: boolean;
}

export function CurrentStatement({
  currencySection: statement,
  loading,
  children,
  organisationTimezone,
  omitColumns,
  childContacts,
  showSplitBalances,
}: ICurrentStatementProps) {

  omitColumns = omitColumns || [];

  const hasPaymentLinks = statement.lines.some(line => !!line.paymentLink);

  function formatAmount(amount: number, currency: string) {
    const formatter = currencyFormatter(currency);

    const isNegative = amount < 0;

    const result = formatter(toDecimal(amount));

    if (isNegative) {
      return `(${result.replace(`-`, ``)})`;
    }

    return formatter(toDecimal(amount));
  }

  if (!statement) {
    return null;
  }

  const colCount = 7 - omitColumns.length;

  const childIds = childContacts && childContacts.length > 0 ? childContacts.map(c => c.id) : [];
  const parentStatementLines = statement.lines.filter(line => !childIds.includes(line.contactId));

  const renderStatementLines = (lines: IStatementLine[], colCount: number, omitColumns: string[], hasPaymentLinks: boolean, childName?: string) => {

    const subtotalOutstanding = lines.reduce((acc, line) => acc + line.outstandingInCents, 0);

    return (
      <tbody className={ `divide-y divide-gray-200 bg-white` }>
        <When condition={ childName }>
          <tr className={ `whitespace-nowrap` }><td>&nbsp;</td></tr>
          <tr>
            <th
              colSpan={ colCount - 1 } // Subtract 1 for the subtotal column
              className={ `whitespace-nowrap py-2 text-sm text-gray-800 font-semibold bg-gray-100` }
            >
              { childName }
            </th>
            <th
              className={ `whitespace-nowrap py-2 text-sm text-gray-800 font-semibold bg-gray-100` }
            >
              { `Subtotal` }
            </th>
            <th
              className={ `whitespace-nowrap py-2 text-sm text-gray-800 font-semibold bg-gray-100` }
            >
              { formatAmount(subtotalOutstanding, statement.currency) }
            </th>
          </tr>
        </When>
        { lines.map((line, idx) => {
          const link = (
            <WrappedLink
              to={ `/invoices/${line.invoiceId}` }>
              <Tooltip title={ line.invoiceNumber }>
                <span>
                  { line.invoiceNumber }
                </span>
              </Tooltip>
              <When condition={ line.frozen }>
                <Tooltip title={ `This invoice has been frozen. It will appear in statements but no other policies will apply.` }>
                  <ExclamationCircleIcon className={ `h-5 w-5 ml-1 text-blue-500` } />
                </Tooltip>
              </When>
            </WrappedLink>
          );

          return (
            <tr
              key={ `${line.invoiceId}_${idx}` }
            >
              <td className={
                classNames(
                  `whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-900 sm:pl-0 truncate max-w-[150px]`,
                  line.type === StatementLineType.Invoice && `underline`,
                )
              }>
                <When condition={ line.type === StatementLineType.Invoice }>
                  { link }
                </When>
                <When condition={ line.type === StatementLineType.CreditNote }>
                  <Tooltip title={ `Credit Note` }>
                    <span className={ `text-orange-700 flex items-center` }>
                      { line.invoiceNumber }

                      <Badge
                        size={ `xs` }
                        color={ `orange` }
                        className={ `ml-1` }
                      >
                        { `C` }
                      </Badge>
                    </span>
                  </Tooltip>
                </When>

                <When condition={ line.type === StatementLineType.Prepayment }>
                  <Tooltip title={ `Prepayment` }>
                    <span className={ `text-blue-700 flex items-center` }>
                      { line.invoiceNumber }

                      <Badge
                        size={ `xs` }
                        color={ `blue` }
                        className={ `ml-1` }
                      >
                        { `P` }
                      </Badge>
                    </span>
                  </Tooltip>
                </When>

                <When condition={ line.type === StatementLineType.Overpayment }>
                  <Tooltip title={ `Overpayment` }>
                    <span className={ `text-green-700 flex items-center` }>
                      { line.invoiceNumber }

                      <Badge
                        size={ `xs` }
                        color={ `green` }
                        className={ `ml-1` }
                      >
                        { `O` }
                      </Badge>
                    </span>
                  </Tooltip>
                </When>
              </td>
              <Tooltip title={ line.invoiceReference }>
                <td className={ `whitespace-nowrap px-2 py-2 text-sm text-gray-900 truncate max-w-[150px]` }>{ line.invoiceReference }</td>
              </Tooltip>
              <td className={ `whitespace-nowrap px-2 py-2 text-sm font-medium text-gray-900` }>
                <DatePopover
                  date={ line.issueDateString }
                  organisationTimezone={ organisationTimezone }
                  labelFormat={ `dd LLL yyyy` }
                />
              </td>
              <td className={ `whitespace-nowrap px-2 py-2 text-sm font-medium text-gray-900` }>
                <When condition={ !!line.invoiceDueDateString }>
                  {
                    () => (
                      <>
                        <DatePopover
                          date={ line.invoiceDueDateString }
                          organisationTimezone={ organisationTimezone }
                          labelFormat={ `dd LLL yyyy` }
                          time={ `end` }
                        />
                      </>
                    )
                  }
                </When>
              </td>
              <td className={ `whitespace-nowrap px-2 py-2 text-sm text-gray-500` }>
                <When condition={ line.debtAgeInDays }>
                  <span className={ classNames(
                    `font-semibold`,
                    line.debtAgeInDays > 0 && `text-orange-500`,
                    line.debtAgeInDays > 30 && `text-red-500`,
                  ) }
                  >
                    { line.debtAgeInDays }
                  </span>
                  { ` days` }
                </When>
              </td>
              <td className={
                classNames(
                  `whitespace-nowrap px-2 py-2 text-sm text-gray-500`,
                  line.type === StatementLineType.CreditNote ? `text-orange-700`: ``,
                )
              }>
                { formatAmount(line.totalInCents, line.currency) }
              </td>
              <When condition={ !omitColumns.includes(`payments`) }>
                <td className={
                  classNames(
                    `whitespace-nowrap px-2 py-2 text-sm text-gray-500`,
                    line.type === StatementLineType.CreditNote ? `text-orange-700`: ``,
                  )
                }>
                  { formatAmount(line.paymentsInCents, line.currency) }
                </td>
              </When>
              <td className={
                classNames(
                  `whitespace-nowrap px-2 py-2 text-sm text-gray-500`,
                  line.type === StatementLineType.CreditNote ? `text-orange-700`: ``,
                )
              }>
                { formatAmount(line.outstandingInCents, line.currency) }
              </td>
              {
                hasPaymentLinks && (
                  <td className={ `whitespace-nowrap px-2 py-2 text-sm text-blue-600` }>
                    <When condition={ line.paymentLink }>
                      <a
                        href={ line.paymentLink }
                        target={ `_blank` }
                        rel={ `noopener noreferrer` }
                        className={ `underline` }
                      >
                        { `Pay Now` }
                      </a>
                    </When>
                  </td>
                )
              }
            </tr>
          );
        }) }
      </tbody>
    );
  };

  return (
    <>

      <LoadingOverlay
        loading={ loading }
      >
        <CardContent>
          <div className={ `mt-8 flow-root` }>
            <div className={ `-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8` }>
              <div className={ `inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8` }>
                <table className={ `min-w-full divide-y divide-gray-300` }>
                  <thead>
                    <tr>
                      <th
                        scope={ `col` }
                        className={ `whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0` }
                      >
                        { `Invoice No.` }
                      </th>
                      <th
                        scope={ `col` }
                        className={ `whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900` }
                      >
                        { `Reference` }
                      </th>
                      <th
                        scope={ `col` }
                        className={ `whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900` }
                      >
                        { `Issue Date` }
                      </th>
                      <th
                        scope={ `col` }
                        className={ `whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900` }
                      >
                        { `Due Date` }
                      </th>
                      <th
                        scope={ `col` }
                        className={ `whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900` }
                      >
                        { `Overdue Age` }
                      </th>
                      <th
                        scope={ `col` }
                        className={ `whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900` }
                      >
                        { `Total` }
                      </th>
                      <When condition={ !omitColumns.includes(`payments`) }>
                        <th
                          scope={ `col` }
                          className={ `whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900` }
                        >
                          { `Payments` }
                        </th>
                      </When>
                      <th
                        scope={ `col` }
                        className={ `whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900` }
                      >
                        { `Outstanding` }
                      </th>
                      {
                        hasPaymentLinks && (
                          <th
                            scope={ `col` }
                            className={ `whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900` }
                          >
                            { `Pay` }
                          </th>
                        )
                      }
                    </tr>
                  </thead>

                  { /* Always first render the parent contact id's lines */ }
                  { renderStatementLines(parentStatementLines, colCount, omitColumns, hasPaymentLinks) }

                  { /* If there's child contacts render each one's invoices, if any */ }
                  <When condition={ childContacts && childContacts.length > 0 }>
                    { childContacts.map(child => {
                      const childLines = statement.lines.filter(line => line.contactId === child.id);
                      { /* If there are no lines for this child, skip rendering */ }
                      if (childLines.length === 0) {
                        return null;
                      }

                      return renderStatementLines(childLines, colCount, omitColumns, hasPaymentLinks, child.name);
                    }) }
                  </When>
                  <tfoot>
                    <tr>
                      <td
                        colSpan={ colCount }
                        className={ `whitespace-nowrap py-2 text-sm text-gray-800 font-semibold` }
                      >
                        <div className={ `flex` }>
                          { `Balance:` }
                          <HelpIcon
                            className={ `ml-0.5` }
                            tooltip={ `This may differ from the total outstanding due to credit notes.` }
                          />
                        </div>
                      </td>
                      <td
                        className={ `whitespace-nowrap px-2 py-2 text-sm text-gray-800 font-semibold` }
                      >
                        { formatAmount(statement.totalOutstandingInCents, statement.currency) }
                      </td>
                    </tr>

                    <tr>
                      <td
                        colSpan={ colCount }
                        className={ `whitespace-nowrap py-2 text-sm text-gray-800 font-semibold` }
                      >
                        { `Total Outstanding:` }
                      </td>
                      <td
                        className={ `whitespace-nowrap px-2 py-2 text-sm text-gray-800 font-semibold` }
                      >
                        { formatAmount(statement.totalOutstandingGrossInCents, statement.currency) }
                      </td>
                    </tr>

                    <When condition={ showSplitBalances }>
                      <tr>
                        <td
                          colSpan={ colCount }
                          className={ `whitespace-nowrap py-2 text-sm text-gray-800 font-semibold` }
                        >
                          { `Total Due Now:` }
                        </td>
                        <td
                          className={ `whitespace-nowrap px-2 py-2 text-sm text-gray-800 font-semibold` }
                        >
                          { formatAmount(getStatementSplitData(statement).before.totalOutstandingGrossInCents, statement.currency) }
                        </td>
                      </tr>

                      <tr>
                        <td
                          colSpan={ colCount }
                          className={ `whitespace-nowrap py-2 text-sm text-gray-800 font-semibold` }
                        >
                          { `Total Not Yet Due:` }
                        </td>
                        <td
                          className={ `whitespace-nowrap px-2 py-2 text-sm text-gray-800 font-semibold` }
                        >
                          { formatAmount(getStatementSplitData(statement).after.totalOutstandingGrossInCents, statement.currency) }
                        </td>
                      </tr>
                    </When>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
          { children }
        </CardContent>
      </LoadingOverlay>
    </>
  );
}
